import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '../contexts/SettingsContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';

const { FiDownload, FiPlay, FiStop, FiTrash2, FiCpu, FiHardDrive, FiZap, FiRefreshCw, FiX, FiAlertTriangle } = FiIcons;
const { BiBot } = BiIcons;

const LocalLLMManager = ({ onClose }) => {
  const { settings, updateSettings } = useSettings();
  const [models, setModels] = useState([]);
  const [loadingModel, setLoadingModel] = useState(null);
  const [runningModel, setRunningModel] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [systemInfo, setSystemInfo] = useState(null);
  const [webLLMLoaded, setWebLLMLoaded] = useState(false);
  const [initializingWebLLM, setInitializingWebLLM] = useState(false);
  const [webLLMError, setWebLLMError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [availableModelsList, setAvailableModelsList] = useState([]);

  // Only the 3 working models
  const availableModels = [
    {
      id: 'TinyLlama-1.1B-Chat-v0.4-q4f16_1-MLC',
      name: 'TinyLlama 1.1B Chat',
      description: 'Ultra-lightweight model for basic tasks and quick responses. Most reliable for first-time setup.',
      size: '0.6GB',
      capabilities: ['Basic chat', 'Simple coding', 'Text completion'],
      recommended: true,
      provider: 'webllm',
      performance: 'Fast',
      quality: 'Good for simple tasks'
    },
    {
      id: 'RedPajama-INCITE-Chat-3B-v1-q4f16_1-MLC',
      name: 'RedPajama Chat 3B',
      description: 'Balanced 3B parameter model with good performance for most coding and creative tasks.',
      size: '1.8GB',
      capabilities: ['Advanced chat', 'Code generation', 'Creative writing', 'Problem solving'],
      recommended: true,
      provider: 'webllm',
      performance: 'Balanced',
      quality: 'Great for most tasks'
    },
    {
      id: 'Llama-2-7b-chat-hf-q4f16_1-MLC',
      name: 'Llama 2 7B Chat',
      description: 'Meta\'s powerful 7B parameter model with excellent reasoning and code generation capabilities.',
      size: '4.0GB',
      capabilities: ['Complex reasoning', 'Advanced coding', 'Creative apps', 'Detailed responses'],
      recommended: true,
      provider: 'webllm',
      performance: 'Slower but powerful',
      quality: 'Excellent for complex tasks'
    }
  ];

  useEffect(() => {
    checkSystemCapabilities();
    loadInstalledModels();
    checkRunningModel();
    initializeWebLLM();
  }, []);

  const checkSystemCapabilities = () => {
    const info = {
      webAssembly: typeof WebAssembly !== 'undefined',
      webGPU: 'gpu' in navigator,
      sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
      webWorkers: typeof Worker !== 'undefined',
      indexedDB: 'indexedDB' in window,
      memoryEstimate: navigator.deviceMemory || 'Unknown',
      userAgent: navigator.userAgent
    };
    setSystemInfo(info);
  };

  const initializeWebLLM = async () => {
    // Check if already loaded
    if (window.webllm || window.tvmjs || window.MLCEngine) {
      console.log('✅ WebLLM already available');
      setWebLLMLoaded(true);
      await fetchAvailableModels();
      return;
    }

    setInitializingWebLLM(true);
    setWebLLMError(null);

    try {
      console.log('🚀 Attempting to load WebLLM library...');
      
      // Updated CDN sources with correct URLs
      const cdnSources = [
        // Primary: Use the ES module from JSDelivr
        'https://cdn.jsdelivr.net/npm/@mlc-ai/web-llm@0.2.46/+esm',
        // Backup: UNPKG ES module
        'https://unpkg.com/@mlc-ai/web-llm@0.2.46/lib/index.js',
        // Fallback: Skypack (ES modules)
        'https://cdn.skypack.dev/@mlc-ai/web-llm@0.2.46',
        // Last resort: ESM.sh
        'https://esm.sh/@mlc-ai/web-llm@0.2.46'
      ];

      let loaded = false;
      
      for (let i = 0; i < cdnSources.length && !loaded; i++) {
        const src = cdnSources[i];
        console.log(`📥 Trying CDN source ${i + 1}/${cdnSources.length}: ${src}`);
        
        try {
          await loadESModuleFromCDN(src);
          
          // Wait for the library to initialize
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Check if WebLLM is now available
          if (window.webllm || window.tvmjs || window.MLCEngine) {
            console.log('✅ WebLLM library loaded successfully from:', src);
            loaded = true;
            setWebLLMLoaded(true);
            setInitializingWebLLM(false);
            await fetchAvailableModels();
            return;
          }
        } catch (error) {
          console.warn(`❌ Failed to load from ${src}:`, error);
          continue;
        }
      }

      // If all CDN sources failed, try alternative approach
      if (!loaded) {
        console.log('🔄 All CDN sources failed, trying alternative approach...');
        await tryAlternativeWebLLMLoad();
      }

    } catch (error) {
      console.error('❌ WebLLM initialization failed:', error);
      setWebLLMError(error.message);
      setInitializingWebLLM(false);
    }
  };

  const fetchAvailableModels = async () => {
    try {
      // Try to get the official list of available models from WebLLM
      if (window.webllm && window.webllm.prebuiltAppConfig) {
        const modelList = window.webllm.prebuiltAppConfig.model_list || [];
        console.log('📋 Available WebLLM models:', modelList.map(m => m.model_id));
        setAvailableModelsList(modelList.map(m => m.model_id));
      } else if (window.MLCEngine) {
        // Try to create an engine to get available models
        try {
          const tempEngine = new window.MLCEngine();
          // Some WebLLM versions expose available models differently
          console.log('🔍 WebLLM engine created, checking for model list...');
        } catch (error) {
          console.warn('Could not create temp engine:', error);
        }
      }
    } catch (error) {
      console.warn('Could not fetch available models list:', error);
    }
  };

  const loadESModuleFromCDN = (src) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Timeout loading ${src}`));
      }, 30000); // 30 second timeout

      // Try loading as ES module
      import(src).then(module => {
        clearTimeout(timeout);
        console.log(`ES Module loaded from: ${src}`, module);
        
        // Store the module globally
        if (module.MLCEngine) {
          window.MLCEngine = module.MLCEngine;
          window.webllm = module;
        }
        
        resolve(module);
      }).catch(error => {
        clearTimeout(timeout);
        console.error(`ES Module import failed for ${src}:`, error);
        reject(error);
      });
    });
  };

  const tryAlternativeWebLLMLoad = async () => {
    try {
      // Create a mock WebLLM implementation for testing
      console.log('🧪 Creating mock WebLLM for testing...');
      
      // Simple mock implementation
      const MockMLCEngine = class {
        constructor() {
          console.log('Mock MLCEngine created');
        }
        
        async reload(modelId, options = {}) {
          console.log('Mock: Loading model', modelId);
          
          // Simulate download progress
          if (options.progressCallback) {
            for (let i = 0; i <= 100; i += 10) {
              await new Promise(resolve => setTimeout(resolve, 200));
              options.progressCallback(i / 100);
            }
          }
          
          console.log('Mock: Model loaded successfully');
        }
        
        async unload() {
          console.log('Mock: Model unloaded');
        }
        
        get chat() {
          return {
            completions: {
              create: async (options) => {
                console.log('Mock: Generating response for:', options.messages);
                
                // Simulate AI response
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                const userMessage = options.messages[options.messages.length - 1].content;
                let response = '';
                
                // More sophisticated mock responses for app creation
                if (userMessage.includes('Create a complete web application')) {
                  // Extract app description from prompt
                  const descMatch = userMessage.match(/description: "([^"]+)"/);
                  const appDesc = descMatch ? descMatch[1] : 'interactive app';
                  
                  // Fixed template literal escaping issue
                  const indexHtml = '<!DOCTYPE html>\\n<html lang="en">\\n<head>\\n  <meta charset="UTF-8">\\n  <meta name="viewport" content="width=device-width, initial-scale=1.0">\\n  <title>Mock Generated App</title>\\n  <link rel="stylesheet" href="style.css">\\n</head>\\n<body>\\n  <div class="container">\\n    <h1>🧪 Mock Generated App</h1>\\n    <p>This is a mock response for: <strong>' + appDesc + '</strong></p>\\n    <p>Real WebLLM would generate a fully functional app based on your description!</p>\\n    <button onclick="mockFunction()">Test Button</button>\\n    <div id="output"></div>\\n  </div>\\n  <script src="script.js"></script>\\n</body>\\n</html>';
                  
                  const styleCss = '* { margin: 0; padding: 0; box-sizing: border-box; }\\n\\nbody { font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; display: flex; align-items: center; justify-content: center; }\\n\\n.container { background: white; padding: 2rem; border-radius: 20px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); text-align: center; max-width: 500px; }\\n\\nh1 { color: #333; margin-bottom: 1rem; font-size: 2rem; }\\n\\np { color: #666; margin-bottom: 1rem; line-height: 1.6; }\\n\\nbutton { background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 16px; cursor: pointer; transition: all 0.3s; }\\n\\nbutton:hover { background: #5a6fd8; transform: translateY(-2px); }\\n\\n#output { margin-top: 1rem; padding: 1rem; background: #f8f9fa; border-radius: 8px; min-height: 50px; }';
                  
                  const scriptJs = 'function mockFunction() {\\n  const output = document.getElementById(\'output\');\\n  const responses = [\\n    \'✨ Mock AI response: This would be a real interactive feature!\',\\n    \'🎉 In real mode, this would connect to your actual app logic!\',\\n    \'🚀 Real WebLLM would generate sophisticated functionality here!\',\\n    \'💡 This demonstrates how local AI can create dynamic apps!\'\\n  ];\\n  \\n  const randomResponse = responses[Math.floor(Math.random() * responses.length)];\\n  output.innerHTML = \'<p style="color: #28a745; font-weight: 500;">\' + randomResponse + \'</p>\';\\n  \\n  // Add some animation\\n  output.style.transform = \'scale(0.95)\';\\n  setTimeout(() => {\\n    output.style.transform = \'scale(1)\';\\n  }, 100);\\n}\\n\\n// Auto-run demo\\ndocument.addEventListener(\'DOMContentLoaded\', function() {\\n  console.log(\'Mock app loaded! Real WebLLM would provide much more sophisticated functionality.\');\\n});';
                  
                  response = `{
  "index.html": "${indexHtml}",
  "style.css": "${styleCss}",
  "script.js": "${scriptJs}"
}`;
                } else if (userMessage.toLowerCase().includes('hello')) {
                  response = 'Hi! I\'m a mock local AI model. Real WebLLM would provide much better responses and can create amazing web applications!';
                } else if (userMessage.toLowerCase().includes('code')) {
                  response = 'I can help with coding! Here\'s a simple example:\\n\\n```javascript\\nfunction greet(name) {\\n  return `Hello, ${name}!`;\\n}\\n```\\n\\nReal WebLLM would provide much more sophisticated code generation!';
                } else {
                  response = `I understand you said: "${userMessage}". I'm a mock AI for testing. Real WebLLM integration would provide professional-quality responses and app generation!`;
                }
                
                return {
                  choices: [{
                    message: {
                      content: response,
                      role: 'assistant'
                    }
                  }]
                };
              }
            }
          };
        }
      };
      
      // Store mock implementation
      window.MLCEngine = MockMLCEngine;
      window.webllm = { MLCEngine: MockMLCEngine };
      
      console.log('✅ Mock WebLLM implementation created');
      setWebLLMLoaded(true);
      setInitializingWebLLM(false);
      
      // Show info about mock mode
      setWebLLMError('Using mock mode - real WebLLM CDN unavailable. Models will simulate responses for testing.');
      
    } catch (error) {
      throw new Error('Failed to initialize WebLLM (both real and mock implementations failed)');
    }
  };

  const retryWebLLMInit = () => {
    setRetryCount(prev => prev + 1);
    setWebLLMError(null);
    setWebLLMLoaded(false);
    setInitializingWebLLM(false);
    
    // Clear any existing WebLLM references
    delete window.webllm;
    delete window.MLCEngine;
    delete window.tvmjs;
    
    // Wait a moment then retry
    setTimeout(() => {
      initializeWebLLM();
    }, 1000);
  };

  const loadInstalledModels = async () => {
    try {
      const request = indexedDB.open('LocalLLMDB', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['models'], 'readonly');
        const store = transaction.objectStore('models');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          setModels(getAllRequest.result || []);
        };
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('models')) {
          db.createObjectStore('models', { keyPath: 'id' });
        }
      };
    } catch (error) {
      console.error('Error loading installed models:', error);
    }
  };

  const checkRunningModel = () => {
    const runningModelId = localStorage.getItem('runningLocalModel');
    if (runningModelId) {
      setRunningModel(runningModelId);
    }
  };

  const downloadModel = async (model) => {
    if (!webLLMLoaded) {
      alert('WebLLM is not loaded yet. Please wait for initialization to complete or try refreshing.');
      return;
    }

    setLoadingModel(model.id);
    setDownloadProgress({ [model.id]: 0 });

    try {
      console.log(`🚀 Starting download of ${model.name} (${model.id})...`);
      
      // Get the MLCEngine class
      const MLCEngine = window.MLCEngine || window.webllm?.MLCEngine || window.webllm?.default?.MLCEngine;
      
      if (!MLCEngine) {
        throw new Error('MLCEngine not found. WebLLM may not be properly loaded.');
      }

      // Initialize WebLLM engine with progress tracking
      const engine = new MLCEngine();
      
      // Set up progress callback
      const progressCallback = (progress) => {
        const percentage = Math.round(progress * 100);
        console.log(`📥 Download progress for ${model.name}: ${percentage}%`);
        setDownloadProgress({ [model.id]: percentage });
      };

      // Download and initialize the model
      console.log(`🔄 Attempting to reload model: ${model.id}`);
      
      try {
        await engine.reload(model.id, {
          progressCallback: progressCallback
        });
      } catch (modelError) {
        console.error(`❌ Model ${model.id} failed to load:`, modelError);
        
        // Check if it's a "model not found" error and suggest alternatives
        if (modelError.message.includes('not found') || modelError.message.includes('404')) {
          throw new Error(`Model "${model.id}" not found on WebLLM servers. This model may not be available anymore. Try TinyLlama instead.`);
        } else {
          throw modelError;
        }
      }

      console.log(`✅ Successfully downloaded and initialized ${model.name}`);

      // Store model info in IndexedDB
      const request = indexedDB.open('LocalLLMDB', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['models'], 'readwrite');
        const store = transaction.objectStore('models');
        
        const modelData = {
          ...model,
          downloadedAt: new Date().toISOString(),
          status: 'downloaded'
        };
        
        store.put(modelData);
        
        transaction.oncomplete = () => {
          setModels(prev => {
            const existing = prev.find(m => m.id === model.id);
            if (existing) {
              return prev.map(m => m.id === model.id ? modelData : m);
            } else {
              return [...prev, modelData];
            }
          });
          setLoadingModel(null);
          setDownloadProgress({});
        };
      };

      // Store engine globally for later use
      window.localLLMEngine = engine;

    } catch (error) {
      console.error('❌ Download error:', error);
      setLoadingModel(null);
      setDownloadProgress({});
      
      // Show user-friendly error message
      let errorMessage = 'Failed to download model. ';
      if (error.message.includes('not found') || error.message.includes('404')) {
        errorMessage += 'This model is not available on WebLLM servers. Try TinyLlama which is most reliable.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage += 'Please check your internet connection.';
      } else if (error.message.includes('memory') || error.message.includes('Memory')) {
        errorMessage += 'Not enough memory. Try closing other tabs or use TinyLlama (smaller model).';
      } else if (error.message.includes('WebGPU')) {
        errorMessage += 'WebGPU not supported. Try a different browser.';
      } else if (error.message.includes('MLCEngine')) {
        errorMessage += 'WebLLM not properly loaded. Try refreshing the page.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  const loadModel = async (modelId) => {
    if (!webLLMLoaded) {
      alert('WebLLM is not loaded yet. Please wait for initialization to complete.');
      return;
    }

    try {
      setLoadingModel(modelId);
      console.log(`🤖 Loading model: ${modelId}`);

      // Get the MLCEngine class
      const MLCEngine = window.MLCEngine || window.webllm?.MLCEngine || window.webllm?.default?.MLCEngine;
      
      if (!MLCEngine) {
        throw new Error('MLCEngine not found. WebLLM may not be properly loaded.');
      }

      // Check if we already have an engine with this model
      if (window.localLLMEngine) {
        try {
          // Try to reload the model in the existing engine
          await window.localLLMEngine.reload(modelId);
        } catch (error) {
          // If reload fails, create a new engine
          console.log('Creating new engine instance...');
          window.localLLMEngine = new MLCEngine();
          await window.localLLMEngine.reload(modelId);
        }
      } else {
        // Create new engine
        window.localLLMEngine = new MLCEngine();
        await window.localLLMEngine.reload(modelId);
      }

      // Set running model
      setRunningModel(modelId);
      localStorage.setItem('runningLocalModel', modelId);

      // Update settings to use local model
      updateSettings({
        ...settings,
        aiProvider: 'local',
        localModelId: modelId
      });

      console.log('✅ Model loaded successfully:', modelId);
      setLoadingModel(null);

      // Test the model with a simple query
      try {
        const testResponse = await window.localLLMEngine.chat.completions.create({
          messages: [{ role: 'user', content: 'Hello! Just say "Hi" to confirm you\'re working.' }],
          max_tokens: 10
        });
        console.log('🧪 Model test successful:', testResponse.choices[0].message.content);
      } catch (testError) {
        console.warn('⚠️ Model test failed, but model should still work:', testError);
      }

    } catch (error) {
      console.error('❌ Error loading model:', error);
      setLoadingModel(null);
      
      let errorMessage = `Failed to load model: `;
      if (error.message.includes('not found') || error.message.includes('404')) {
        errorMessage += 'Model not available. Try downloading it first or use a different model.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  const unloadModel = async () => {
    try {
      if (window.localLLMEngine) {
        // Unload the current model
        await window.localLLMEngine.unload();
        console.log('🔄 Model unloaded successfully');
      }
      
      setRunningModel(null);
      localStorage.removeItem('runningLocalModel');
      
      // Update settings
      updateSettings({
        ...settings,
        localModelId: null
      });
      
    } catch (error) {
      console.error('Error unloading model:', error);
      // Force clear even if unload fails
      setRunningModel(null);
      localStorage.removeItem('runningLocalModel');
    }
  };

  const deleteModel = async (modelId) => {
    if (!confirm(`Are you sure you want to delete ${modelId}? This will remove all downloaded data.`)) {
      return;
    }

    try {
      // If this model is currently running, unload it first
      if (runningModel === modelId) {
        await unloadModel();
      }

      // Remove from IndexedDB
      const request = indexedDB.open('LocalLLMDB', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['models'], 'readwrite');
        const store = transaction.objectStore('models');
        store.delete(modelId);
        
        transaction.oncomplete = () => {
          setModels(prev => prev.filter(m => m.id !== modelId));
        };
      };

    } catch (error) {
      console.error('Error deleting model:', error);
      alert(`Failed to delete model: ${error.message}`);
    }
  };

  const isModelInstalled = (modelId) => {
    return models.some(m => m.id === modelId);
  };

  const getModelStatus = (modelId) => {
    if (runningModel === modelId) return 'running';
    if (loadingModel === modelId) return 'loading';
    if (isModelInstalled(modelId)) return 'installed';
    return 'available';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-green-400';
      case 'loading': return 'text-yellow-400';
      case 'installed': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return FiPlay;
      case 'loading': return FiRefreshCw;
      case 'installed': return FiHardDrive;
      default: return FiDownload;
    }
  };

  const canUseWebLLM = () => {
    return systemInfo?.webAssembly && systemInfo?.webWorkers && systemInfo?.indexedDB;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <SafeIcon icon={BiBot} className="mr-3 text-purple-400 text-2xl" />
            <div>
              <h3 className="text-2xl font-bold text-white">Local AI Model Manager</h3>
              <p className="text-gray-400 text-sm mt-1">
                Download and run AI models directly in your browser
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* WebLLM Initialization Status */}
          {initializingWebLLM && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                  <span className="text-yellow-300 font-medium">Loading WebLLM library from CDN...</span>
                </div>
                <div className="text-yellow-200 text-sm">
                  Attempt {retryCount + 1}
                </div>
              </div>
              <p className="text-yellow-200 text-sm mt-2">
                This may take up to 30 seconds. Trying multiple CDN sources...
              </p>
            </div>
          )}

          {/* WebLLM Error/Warning */}
          {webLLMError && (
            <div className={`mb-6 p-4 ${webLLMError.includes('mock mode') ? 'bg-orange-500/10 border-orange-500/30' : 'bg-red-500/10 border-red-500/30'} rounded-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <SafeIcon icon={FiAlertTriangle} className={`mr-2 ${webLLMError.includes('mock mode') ? 'text-orange-400' : 'text-red-400'}`} />
                    <span className={`font-medium ${webLLMError.includes('mock mode') ? 'text-orange-300' : 'text-red-300'}`}>
                      {webLLMError.includes('mock mode') ? 'WebLLM Mock Mode Active' : 'WebLLM Loading Failed'}
                    </span>
                  </div>
                  <p className={`text-sm mb-3 ${webLLMError.includes('mock mode') ? 'text-orange-200' : 'text-red-200'}`}>{webLLMError}</p>
                  {!webLLMError.includes('mock mode') && (
                    <div className="text-red-200 text-xs">
                      <p>• Make sure you have a stable internet connection</p>
                      <p>• Try using Chrome or Edge browser (recommended)</p>
                      <p>• Check if your firewall or ad blocker is blocking CDN requests</p>
                      <p>• The CDN services might be temporarily unavailable</p>
                    </div>
                  )}
                  {webLLMError.includes('mock mode') && (
                    <div className="text-orange-200 text-xs">
                      <p>• Mock mode provides simulated AI responses for testing</p>
                      <p>• Real WebLLM requires stable CDN access</p>
                      <p>• Try refreshing the page or check your network connection</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={retryWebLLMInit}
                  className={`ml-4 px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    webLLMError.includes('mock mode') 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* System Compatibility */}
          {systemInfo && (
            <div className="mb-6 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
              <h4 className="text-white font-semibold mb-3 flex items-center">
                <SafeIcon icon={FiCpu} className="mr-2 text-blue-400" />
                Browser Compatibility
              </h4>
              
              {!canUseWebLLM() && (
                <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="mr-2 text-red-400" />
                    <span className="text-red-300 font-medium">
                      Your browser doesn't support all required features for local AI models.
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">WebAssembly:</span>
                  <span className={systemInfo.webAssembly ? 'text-green-400' : 'text-red-400'}>
                    {systemInfo.webAssembly ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">WebGPU:</span>
                  <span className={systemInfo.webGPU ? 'text-green-400' : 'text-yellow-400'}>
                    {systemInfo.webGPU ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">SharedArrayBuffer:</span>
                  <span className={systemInfo.sharedArrayBuffer ? 'text-green-400' : 'text-yellow-400'}>
                    {systemInfo.sharedArrayBuffer ? '✓' : '⚠'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Web Workers:</span>
                  <span className={systemInfo.webWorkers ? 'text-green-400' : 'text-red-400'}>
                    {systemInfo.webWorkers ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">IndexedDB:</span>
                  <span className={systemInfo.indexedDB ? 'text-green-400' : 'text-red-400'}>
                    {systemInfo.indexedDB ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">RAM:</span>
                  <span className="text-blue-400 font-medium">
                    {systemInfo.memoryEstimate !== 'Unknown' ? `${systemInfo.memoryEstimate}GB` : 'Unknown'}
                  </span>
                </div>
              </div>
              
              <div className="mt-3 text-xs text-gray-400">
                <p>• WebLLM Status: {webLLMLoaded ? (webLLMError?.includes('mock mode') ? '🧪 Mock Mode' : '✅ Ready') : webLLMError ? '❌ Failed' : '⏳ Loading...'}</p>
                <p>• Browser: {systemInfo.userAgent.includes('Chrome') ? 'Chrome' : systemInfo.userAgent.includes('Firefox') ? 'Firefox' : systemInfo.userAgent.includes('Safari') ? 'Safari' : 'Other'}</p>
                <p>• Recommended: Chrome/Edge with 4GB+ RAM for best performance</p>
                <p>• Only 3 verified working models are shown below</p>
              </div>
            </div>
          )}

          {/* Current Running Model */}
          {runningModel && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <h4 className="text-green-400 font-semibold mb-2 flex items-center">
                <SafeIcon icon={FiZap} className="mr-2" />
                Currently Running Model
              </h4>
              <div className="text-sm text-green-200">
                <p>✓ Active Model: <strong>{runningModel}</strong></p>
                <p>✓ AI Provider: <strong>Local (Browser)</strong></p>
                <p>✓ Status: <strong>Ready for AI features</strong></p>
                <p className="mt-2 text-xs">Your AI assistant and app creator are now using this local model!</p>
              </div>
            </div>
          )}

          {/* Available Models */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center">
              <SafeIcon icon={FiZap} className="mr-2 text-yellow-400" />
              Verified Working Models (3 Total)
            </h4>
            
            {!canUseWebLLM() ? (
              <div className="text-center py-8 text-gray-400">
                <SafeIcon icon={FiAlertTriangle} className="text-4xl mb-3 mx-auto text-red-400" />
                <p className="text-lg mb-2">Browser Not Compatible</p>
                <p className="text-sm">
                  Your browser doesn't support the required features for local AI models.
                  Please use a modern browser like Chrome, Edge, or Firefox.
                </p>
              </div>
            ) : !webLLMLoaded && !webLLMError ? (
              <div className="text-center py-8 text-gray-400">
                <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-lg mb-2">Loading WebLLM Library</p>
                <p className="text-sm">Please wait while we initialize the AI runtime from CDN...</p>
                <p className="text-xs text-gray-500 mt-2">This may take up to 30 seconds on first load</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {availableModels.map((model) => {
                  const status = getModelStatus(model.id);
                  const StatusIcon = getStatusIcon(status);
                  const progress = downloadProgress[model.id];

                  return (
                    <motion.div
                      key={model.id}
                      className="p-5 rounded-xl border bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/30 hover:border-purple-400/50 transition-all duration-200"
                      whileHover={{ y: -2 }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-semibold text-white">{model.name}</h5>
                            <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded-full font-medium">
                              Verified ✓
                            </span>
                            {webLLMError?.includes('mock mode') && (
                              <span className="px-2 py-1 bg-orange-500/20 text-orange-300 text-xs rounded-full font-medium">
                                Mock Mode
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mb-2">{model.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                            <span className="flex items-center gap-1">
                              <SafeIcon icon={FiHardDrive} />
                              {model.size}
                            </span>
                            <span className="flex items-center gap-1">
                              <SafeIcon icon={FiCpu} />
                              {model.performance}
                            </span>
                          </div>
                          <div className="text-xs text-purple-300 font-medium">
                            {model.quality}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <SafeIcon
                            icon={StatusIcon}
                            className={`text-lg ${getStatusColor(status)} ${
                              status === 'loading' ? 'animate-spin' : ''
                            }`}
                          />
                        </div>
                      </div>

                      {/* Capabilities */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1">
                          {model.capabilities.map((capability, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-md"
                            >
                              {capability}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      {progress !== undefined && (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>{webLLMError?.includes('mock mode') ? 'Simulating download...' : 'Downloading model files...'}</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {webLLMError?.includes('mock mode') 
                              ? 'Mock download for testing purposes'
                              : 'This may take several minutes depending on your internet connection'
                            }
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        {status === 'available' && (
                          <button
                            onClick={() => downloadModel(model)}
                            disabled={loadingModel === model.id}
                            className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg font-medium text-sm transition-colors duration-200"
                          >
                            <SafeIcon icon={FiDownload} className="mr-2 text-sm" />
                            {webLLMError?.includes('mock mode') ? 'Mock Download' : 'Download'}
                          </button>
                        )}

                        {status === 'installed' && (
                          <button
                            onClick={() => loadModel(model.id)}
                            disabled={loadingModel === model.id}
                            className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg font-medium text-sm transition-colors duration-200"
                          >
                            <SafeIcon icon={FiPlay} className="mr-2 text-sm" />
                            Load Model
                          </button>
                        )}

                        {status === 'running' && (
                          <button
                            onClick={unloadModel}
                            className="flex-1 flex items-center justify-center px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium text-sm transition-colors duration-200"
                          >
                            <SafeIcon icon={FiStop} className="mr-2 text-sm" />
                            Unload
                          </button>
                        )}

                        {isModelInstalled(model.id) && (
                          <button
                            onClick={() => deleteModel(model.id)}
                            disabled={runningModel === model.id}
                            className="px-3 py-2 bg-gray-700 hover:bg-red-600 disabled:bg-gray-800 disabled:text-gray-500 rounded-lg transition-colors duration-200"
                            title="Delete Model"
                          >
                            <SafeIcon icon={FiTrash2} className="text-sm" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Usage Instructions */}
          {webLLMLoaded && (
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <h5 className="text-blue-400 font-semibold mb-2 flex items-center">
                <SafeIcon icon={FiZap} className="mr-2" />
                How to Use Local Models
              </h5>
              <ul className="text-sm text-blue-200 space-y-1">
                <li>1. <strong>Download</strong> a model {webLLMError?.includes('mock mode') ? '(simulated in mock mode)' : '(downloads real model files to your browser)'}</li>
                <li>2. <strong>Load</strong> the model to make it active</li>
                <li>3. Go to <strong>Settings</strong> and ensure AI Provider is set to "Local LLM"</li>
                <li>4. Use the <strong>AI Assistant</strong> and <strong>AI App Creator</strong> - they'll now use your local model!</li>
                <li>• {webLLMError?.includes('mock mode') ? 'Mock mode provides simulated responses for testing' : 'Models are stored in your browser and work completely offline'}</li>
                <li>• <strong>TinyLlama</strong>: Most reliable, fastest setup, good for basic apps</li>
                <li>• <strong>RedPajama 3B</strong>: Best balance of speed and quality for most projects</li>
                <li>• <strong>Llama 2 7B</strong>: Highest quality responses, best for complex apps (needs more RAM)</li>
              </ul>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LocalLLMManager;